import cx from 'classnames'
import { type IconName, iconsMap } from '@/components/Icon/icons.g'
import styles from './Icon.module.css'
import { Skeleton, useIsSkeleton } from '@/components/Skeleton'

/**
 * Name of an icon.
 */
export type { IconName } from './icons.g'

/**
 * Icon variant.
 */
export type IconVariant =
  | 'active'
  | 'danger'
  | 'disabled'
  | 'invert'
  | 'secondary'
  | 'success'
  | 'inherit'
  | 'surface'
  | 'surface-background'
  | 'surface-active'
  | 'surface-variant'

/**
 * Variant for a skeleton icon.
 */
export type IconSkeletonVariant = boolean | 'full'

export interface IconProps {
  /**
   * Which icon to use.
   */
  name: IconName

  /**
   * Whether to render the icon in a smaller variant.
   * The size depends on which icon is chosen.
   */
  sm?: boolean

  /**
   * Downsizes a regular icon to the mini sizing despite not being
   * originally designed for it... Yeah.
   */
  mini?: boolean

  /**
   * Determines the color to use for the icon.
   */
  variant?: IconVariant

  /**
   * Renders a skeleton instead of the icon. If `full`, renders to the
   * full size of the icon's bounding box.
   */
  skeleton?: IconSkeletonVariant

  /**
   * Rotate the icon by the given number of degrees.
   */
  rotate?: number

  /**
   * Whether to render as an inline element.
   */
  inline?: boolean
}

/**
 * An SVG icon from the design system.
 */
export function Icon({
  name,
  skeleton,
  sm,
  mini,
  rotate,
  inline,
  variant = 'inherit',
}: IconProps) {
  const isSkeleton = useIsSkeleton(skeleton && true)
  const registration = iconsMap.get(name)
  if (!registration) {
    return null
  }

  const Component = registration.Component
  const skeletonSize = sm
    ? 'var(--effective-icon-sm-size)'
    : 'var(--effective-icon-size)'
  const calculatedSkeletonSize =
    skeleton === 'full' ? skeletonSize : `calc(${skeletonSize} * 0.6)`
  return (
    <div
      aria-hidden
      className={cx(
        styles.icon,
        (registration.mini || mini) && styles.mini,
        sm && styles.sm,
        styles[variant],
        inline && styles.inline,
      )}
      style={{
        transform:
          typeof rotate === 'number' ? `rotate(${rotate}deg)` : undefined,
      }}
    >
      {isSkeleton ? (
        <Skeleton
          variant="rect"
          width={calculatedSkeletonSize}
          height={calculatedSkeletonSize}
          // Slight correction for known icons for optical centering.
          topCorrection={name === 'chevron' ? 2 : 0}
        />
      ) : (
        <Component />
      )}
    </div>
  )
}
